import { AuthService } from '../services/AuthService';
import { syncItemAudioForFile } from '../services/ItemAudioSync';
import { assertAllPendingVideosHaveThumbs, syncItemVideoForFile } from '../services/ItemVideoSync';
import { syncItemImagesForFile } from '../services/ItemImageSync';
import { migratePageAssets } from '../services/PageAssetMigration';
import { slugFromLabel } from '../utils/markdownPaths';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import { appAlert, appConfirm, appPrompt } from '../utils/appDialog';
import { openImportMarkdownModal } from '../utils/importMarkdownModal';
import { clearUnsavedItemEdits } from '../utils/itemEditorDraft';
import type { DatabaseFile } from '../types';
import { patchNavOrderPathsForRename, removePathFromNavOrder } from '../utils/navOrder';
import { runOptimisticGithubWrite } from '../utils/optimisticGithubWrite';
import { openHttpDebugModal } from '../utils/httpDebugLog';
import {
  beginSaveRun,
  endSaveRun,
  getActiveSaveRunId,
  logSaveDebug,
  saveDebugStep,
  saveMediaCounts,
} from '../utils/saveDebugLog';
import { DATABASE_INIT_FILES, DB_PREFIX, DASHBOARD_HTML_PATH, markdownPathFromImportName } from './constants';
import type { AppContext } from './appContext';
import {
  clearPendingItemMoves,
  revertPendingItemMoveSideFiles,
  saveFileAndPendingMoves,
} from './pendingItemMoves';
import { showAppHome } from './appViews';

export async function ensureDatabaseLayoutIfEmpty(ctx: AppContext, paths: string[]): Promise<string[]> {
    if (paths.length > 0) return paths;
    for (const { path, body } of DATABASE_INIT_FILES) {
      await ctx.github.writeFile(path, body, '', `DailyLife: initialize ${path}`);
    }
    const again = await ctx.github.listMarkdownFiles(DB_PREFIX);
    if (again.length === 0) {
      throw new Error(
        'Could not create starter files under database/. Check that the token has Contents write access to the repository.'
      );
    }
    return again;
}

export async function refreshDailyDashboardAvailability(ctx: AppContext): Promise<void> {
    try {
      const sha = await ctx.github.getBlobSha(DASHBOARD_HTML_PATH);
      ctx.dailyDashboardAvailable = sha !== null;
    } catch {
      ctx.dailyDashboardAvailable = false;
    }
    ctx.sidebar.setDailyDashboardAvailable(ctx.dailyDashboardAvailable);
}

export async function loadFiles(ctx: AppContext): Promise<void> {
    const sidebarEl = ctx.root.querySelector<HTMLElement>('#sidebar')!;
    const mainEl    = ctx.root.querySelector<HTMLElement>('#main')!;

    try {
      await ctx.github.ensureBranchExists();
      ctx.revokeDashboardBlob();
      clearPendingItemMoves(ctx);
      ctx.fileRegistry.files = [];
      let allPaths = await ctx.github.listMarkdownFiles(DB_PREFIX);
      allPaths = await ensureDatabaseLayoutIfEmpty(ctx, allPaths);
      ctx.allPaths = [...allPaths].sort((a, b) => a.localeCompare(b));
      ctx.invalidateSearchIndex();

      await ctx.navOrder.load();
      await ctx.newsFeedConfig.load();
      await refreshDailyDashboardAvailability(ctx);
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);

      await showAppHome(ctx, sidebarEl, mainEl);
    } catch (err) {
      mainEl.innerHTML = `
        <div class="error-state">
          <p>⚠ Could not load files: ${(err as Error).message}</p>
          <button class="btn-primary" id="retry-btn">Retry</button>
          <button class="btn-ghost" id="debug-btn2">Debug</button>
          <button class="btn-ghost" id="logout-btn2">Sign out</button>
        </div>
      `;
      mainEl.querySelector('#retry-btn')?.addEventListener('click', () => loadFiles(ctx));
      mainEl.querySelector('#debug-btn2')?.addEventListener('click', () => openHttpDebugModal());
      mainEl.querySelector('#logout-btn2')?.addEventListener('click', () => {
        AuthService.clear(); window.location.reload();
      });
    }
}

export async function deleteFile(ctx: AppContext, file: DatabaseFile, sidebarEl: HTMLElement): Promise<void> {
    await ctx.github.deleteFile(file.path, file.sha, `Delete ${file.path.split('/').pop()}`);

    ctx.fileRegistry.files = ctx.fileRegistry.files.filter(f => f.path !== file.path);
    ctx.allPaths = ctx.allPaths.filter(p => p !== file.path);
    ctx.fileRegistry.originalContents.delete(file.path);
    ctx.navOrderFile = removePathFromNavOrder(ctx.navOrderFile, file.path);
    try {
      await ctx.navOrder.persist();
    } catch {
      /* order file may not exist yet */
    }
    ctx.invalidateSearchIndex();
    ctx.sidebar.setActivePath('');
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function importMarkdownInDirectory(ctx: AppContext, parentDir: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    if (parentDir !== 'database' && !parentDir.startsWith('database/')) return;

    const draft = await openImportMarkdownModal();
    if (!draft) return;

    const path = markdownPathFromImportName(parentDir, draft.fileName);
    if (!path) {
      await appAlert('Enter a file name with at least one letter or number.', 'Import failed');
      return;
    }

    const content = draft.content.replace(/\r\n/g, '\n');
    if (!content.trim()) {
      await appAlert('Add some markdown content to import.', 'Import failed');
      return;
    }

    let replaceSha = '';
    if (ctx.allPaths.includes(path)) {
      const ok = await appConfirm(
        `Replace existing file ${path} with the imported content?`,
        { title: 'File already exists', confirmLabel: 'Replace', danger: true }
      );
      if (!ok) return;
      const existing = ctx.fileRegistry.files.find(f => f.path === path);
      if (existing) {
        replaceSha = existing.sha;
        ctx.fileRegistry.files = ctx.fileRegistry.files.filter(f => f.path !== path);
      }
    }

    const imported = ctx.markdown.parse(path, content, replaceSha);
    const snapshotPaths = [...ctx.allPaths];
    const snapshotFiles = [...ctx.fileRegistry.files];

    await runOptimisticGithubWrite({
      applyLocal: () => {
        if (!ctx.allPaths.includes(path)) ctx.allPaths.push(path);
        ctx.allPaths.sort((a, b) => a.localeCompare(b));
        ctx.invalidateSearchIndex();
        ctx.fileRegistry.originalContents.set(path, content);
        ctx.fileRegistry.files.push(imported);
        ctx.sidebar.setActivePath(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        ctx.fileView.render(mainEl, imported);
      },
      rollback: () => {
        ctx.allPaths = snapshotPaths;
        ctx.fileRegistry.files = snapshotFiles;
        ctx.fileRegistry.originalContents.delete(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      },
      write: async () => {
        void (await ctx.github.writeFile(path, content, replaceSha, `Import: ${path.split('/').pop()}`));
      },
      onSuccess: async () => {
        const { sha } = await ctx.github.readFile(path);
        imported.sha = sha;
        ctx.fileRegistry.originalContents.set(path, content);
      },
      onFailure: async err => {
        await appAlert(`Could not upload import: ${err.message}`, 'Import failed');
      },
    });
}

export async function createNewMarkdownInDirectory(ctx: AppContext, parentDir: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    const name = await appPrompt('Choose a name for the new file.', {
      title: 'New file',
      inputLabel: 'File name',
      placeholder: 'e.g. garden notes',
      confirmLabel: 'Create',
    });
    if (!name) return;

    const slug = slugFromLabel(name);
    if (!slug) {
      await appAlert('Use at least one letter or number in the name.');
      return;
    }

    const path = `${parentDir}/${slug}.md`;
    if (ctx.allPaths.includes(path)) {
      await appAlert(`A file already exists at ${path}.`);
      return;
    }

    const content = `# ${name.trim()}\n\n## Items\n\nAdd checklist lines below.\n\n`;
    const newFile = ctx.markdown.parse(path, content, '');
    const snapshotPaths = [...ctx.allPaths];
    const snapshotFiles = [...ctx.fileRegistry.files];

    await runOptimisticGithubWrite({
      applyLocal: () => {
        ctx.allPaths.push(path);
        ctx.allPaths.sort((a, b) => a.localeCompare(b));
        ctx.invalidateSearchIndex();
        ctx.fileRegistry.originalContents.set(path, content);
        ctx.fileRegistry.files.push(newFile);
        ctx.sidebar.setActivePath(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        ctx.fileView.render(mainEl, newFile);
      },
      rollback: () => {
        ctx.allPaths = snapshotPaths;
        ctx.fileRegistry.files = snapshotFiles;
        ctx.fileRegistry.originalContents.delete(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      },
      write: async () => {
        void (await ctx.github.writeFile(path, content, '', `New file: ${path}`));
      },
      onSuccess: async () => {
        const { sha } = await ctx.github.readFile(path);
        newFile.sha = sha;
      },
      onFailure: async err => {
        mainEl.innerHTML = `
          <div class="error-state">
            <p>⚠ Could not create file: ${err.message}</p>
          </div>`;
      },
    });
}

export async function createNewFolderInDirectory(ctx: AppContext, parentDir: string, sidebarEl: HTMLElement, mainEl: HTMLElement): Promise<void> {
    if (parentDir !== 'database' && !parentDir.startsWith('database/')) return;

    const label = await appPrompt('Choose a name for the new folder.', {
      title: 'New folder',
      inputLabel: 'Folder name',
      placeholder: 'e.g. projects',
      confirmLabel: 'Create',
    });
    if (!label) return;

    const folderSlug = slugFromLabel(label);
    if (!folderSlug) {
      await appAlert('Use at least one letter or number in the folder name.');
      return;
    }

    const newDir = `${parentDir}/${folderSlug}`;
    const path = `${newDir}/${folderSlug}.md`;

    if (ctx.allPaths.includes(`${newDir}.md`)) {
      await appAlert(
        `A file already exists at ${newDir}, which blocks a folder with this name in Git. Rename or move that file first.`
      );
      return;
    }
    if (ctx.allPaths.some(p => p.startsWith(`${newDir}/`))) {
      await appAlert(`A folder already exists at ${newDir}/.`);
      return;
    }
    if (ctx.allPaths.includes(path)) {
      await appAlert(`A file already exists at ${path}.`);
      return;
    }

    const title = label;
    const content = `# ${title}\n\n## Items\n\nAdd checklist lines below.\n\n`;
    const newFile = ctx.markdown.parse(path, content, '');
    const snapshotPaths = [...ctx.allPaths];
    const snapshotFiles = [...ctx.fileRegistry.files];

    await runOptimisticGithubWrite({
      applyLocal: () => {
        ctx.allPaths.push(path);
        ctx.allPaths.sort((a, b) => a.localeCompare(b));
        ctx.invalidateSearchIndex();
        ctx.fileRegistry.originalContents.set(path, content);
        ctx.fileRegistry.files.push(newFile);
        ctx.sidebar.setActivePath(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        ctx.fileView.render(mainEl, newFile);
      },
      rollback: () => {
        ctx.allPaths = snapshotPaths;
        ctx.fileRegistry.files = snapshotFiles;
        ctx.fileRegistry.originalContents.delete(path);
        ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
      },
      write: async () => {
        void (await ctx.github.writeFile(path, content, '', `New folder: ${newDir}`));
      },
      onSuccess: async () => {
        const { sha } = await ctx.github.readFile(path);
        newFile.sha = sha;
      },
      onFailure: async err => {
        mainEl.innerHTML = `
          <div class="error-state">
            <p>⚠ Could not create folder: ${err.message}</p>
          </div>`;
      },
    });
}

export async function renameFile(ctx: AppContext, file: DatabaseFile, newName: string, sidebarEl: HTMLElement): Promise<void> {
    const oldPath  = file.path;
    const dir      = oldPath.substring(0, oldPath.lastIndexOf('/') + 1);
    const slug     = slugFromLabel(newName);
    const newPath  = `${dir}${slug}.md`;
    const content  = ctx.fileRegistry.originalContents.get(oldPath) ?? '';

    await ctx.github.renameFile(oldPath, newPath, content, file.sha);
    try {
      await migratePageAssets(ctx.github, oldPath, newPath);
    } catch (err) {
      console.warn('Image folder migration after rename failed:', err);
    }

    ctx.fileRegistry.originalContents.delete(oldPath);
    ctx.fileRegistry.originalContents.set(newPath, content);
    file.path = newPath;
    file.name = newName;

    const i = ctx.allPaths.indexOf(oldPath);
    if (i >= 0) ctx.allPaths[i] = newPath;
    ctx.allPaths.sort((a, b) => a.localeCompare(b));

    ctx.navOrderFile = patchNavOrderPathsForRename(ctx.navOrderFile, oldPath, newPath);
    try {
      await ctx.navOrder.persist();
    } catch {
      /* ignore */
    }

    ctx.invalidateSearchIndex();
    ctx.sidebar.setActivePath(newPath);
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
}

export async function discardOpenFile(ctx: AppContext, file: DatabaseFile, mainEl: HTMLElement): Promise<void> {
    const content = ctx.fileRegistry.originalContents.get(file.path);
    if (content === undefined) {
      void appAlert('No saved version to restore.');
      return;
    }
    clearUnsavedItemEdits(file.path);
    revertPendingItemMoveSideFiles(ctx, file.path);
    clearPendingItemMoves(ctx);
    ctx.revokeImageObjectUrls();
    const fresh = ctx.markdown.parse(file.path, content, file.sha);
    const idx = ctx.fileRegistry.files.findIndex(f => f.path === file.path);
    if (idx >= 0) ctx.fileRegistry.files[idx] = fresh;
    ctx.fileView.render(mainEl, fresh);
}

export async function saveOpenFile(ctx: AppContext, file: DatabaseFile): Promise<void> {
  beginSaveRun(file.path);
  try {
    await saveFileAndPendingMoves(ctx, file);
    endSaveRun(true);
  } catch (err) {
    endSaveRun(false, err);
    throw err;
  }
}

export async function saveFile(ctx: AppContext, file: DatabaseFile): Promise<void> {
    const ownRun = !getActiveSaveRunId();
    if (ownRun) beginSaveRun(file.path);
    try {
      const original = ctx.fileRegistry.originalContents.get(file.path);
      if (!original) {
        const msg = 'Original content not found for diff';
        logSaveDebug({
          phase: 'validate-fail',
          path: file.path,
          ok: false,
          message: msg,
          error: msg,
        });
        throw new Error(msg);
      }
      logSaveDebug({
        phase: 'validate',
        path: file.path,
        ok: true,
        message: 'Original content loaded',
        details: {
          originalLen: original.length,
          fileSha: file.sha ? `${file.sha.slice(0, 8)}…` : '(empty)',
        },
      });
      logSaveDebug({
        phase: 'media-pending',
        path: file.path,
        ok: true,
        message: 'Pending media counts',
        details: saveMediaCounts(file),
      });

      await saveDebugStep('video-thumbs', file.path, async () => {
        assertAllPendingVideosHaveThumbs(file);
      });
      await saveDebugStep('sync-images', file.path, () => syncItemImagesForFile(ctx.github, file));
      await saveDebugStep('sync-audio', file.path, () => syncItemAudioForFile(ctx.github, file));
      await saveDebugStep('sync-video', file.path, () => syncItemVideoForFile(ctx.github, file));

      const updated = await saveDebugStep('serialize', file.path, async () =>
        ctx.markdown.serialize(file, original)
      , {
        originalLen: original.length,
      });
      logSaveDebug({
        phase: 'serialize-size',
        path: file.path,
        ok: true,
        message: `Serialized ${updated.length} chars`,
        details: { originalLen: original.length, updatedLen: updated.length },
      });

      await saveDebugStep('write-file', file.path, () =>
        ctx.github.writeFile(
          file.path,
          updated,
          file.sha,
          `App: update ${file.path.split('/').pop()}`
        )
      , {
        contentLen: updated.length,
        sha: file.sha ? `${file.sha.slice(0, 8)}…` : '(empty)',
      });

      ctx.fileRegistry.originalContents.set(file.path, updated);
      const { content, sha } = await saveDebugStep('re-read', file.path, () =>
        ctx.github.readFile(file.path)
      );
      logSaveDebug({
        phase: 're-read-meta',
        path: file.path,
        ok: true,
        message: 'Re-read after write',
        details: {
          newSha: sha ? `${sha.slice(0, 8)}…` : '(empty)',
          contentLen: content.length,
        },
      });
      ctx.fileRegistry.originalContents.set(file.path, content);
      file.sha = sha;
      clearUnsavedItemEdits(file.path);
      ctx.invalidateSearchIndex();
      ctx.fileView.refreshMediaIfCurrent(file);

      if (ownRun) endSaveRun(true);
    } catch (err) {
      if (ownRun) endSaveRun(false, err);
      throw err;
    }
}